import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Archive,
  Plus,
  SquaresFour,
  Rows,
  NavigationArrow,
  CalendarBlank,
  Wrench,
  MagnifyingGlass,
  TrendDown,
  X,
} from "@phosphor-icons/react";
import { SegTabs } from "@/components/SegTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppHeader } from "@/components/AppHeader";
import { JOB_STAGES, STAGE_DOT } from "@/components/StageBadge";
import { isStalled } from "@/components/StageAge";
import { JobStageBoard } from "@/components/JobStageBoard";
import Projects from "@/pages/Projects";
import { JobFieldView } from "@/components/JobFieldView";
import { isUnprofitable } from "@/components/MarginChip";
import { api } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";

const TABS = [
  { key: "list", label: "List", icon: Rows },
  { key: "field", label: "Field", icon: NavigationArrow },
  { key: "schedule", label: "Schedule", icon: CalendarBlank, to: "/projects/schedule" },
  { key: "service", label: "Service", icon: Wrench, to: "/projects/service" },
  { key: "board", label: "Board", icon: SquaresFour, to: "/projects/board" },
];
const PANELS = new Set(["list", "field"]);

export default function JobBoard({ boardOnly = false }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = boardOnly ? "board" : PANELS.has(params.get("tab")) ? params.get("tab") : "list";

  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("All");
  const [onlyStalled, setOnlyStalled] = useState(false);
  const [onlyLosing, setOnlyLosing] = useState(false);
  const [pay, setPay] = useState("all"); // all | paid | unpaid
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/jobs", {
      params: { ...(q.trim() ? { q: q.trim() } : {}), ...(showArchived ? { archived: true } : {}) },
    });
    setJobs(data);
  }, [q, showArchived]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  usePoll(load);

  const move = async (jobId, stage) => {
    const job = jobs.find((j) => j.id === jobId);
    if (!job || job.stage === stage) return;
    setJobs((list) => list.map((j) => (j.id === jobId ? { ...j, stage } : j)));
    try {
      await api.patch(`/jobs/${jobId}/stage`, { stage });
      toast.success(`${job.code} moved to ${stage}`);
    } catch (err) {
      toast.error("Could not move job");
      load();
    }
  };

  const stalledCount = jobs.filter(isStalled).length;
  const losingCount = jobs.filter(isUnprofitable).length;
  const isPaid = (j) => (j.money?.invoiced || 0) > 0 && (j.money?.balance_due || 0) <= 0.009;
  const shown = jobs.filter((j) =>
    pay === "all"
      ? true
      : j.stage === "Complete" && (pay === "paid" ? isPaid(j) : (j.money?.balance_due || 0) > 0.009),
  );
  const paidCount = jobs.filter((j) => j.stage === "Complete" && isPaid(j)).length;
  const unpaidCount = jobs.filter((j) => j.stage === "Complete" && (j.money?.balance_due || 0) > 0.009).length;

  const goTab = (t) => {
    if (t.to) return navigate(t.to);
    setParams((p) => {
      p.set("tab", t.key);
      return p;
    });
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title={boardOnly ? "Project board" : "Projects"} />
      <main className="full-bleed relative z-10 mx-auto max-w-[100rem] px-4 pb-28 pt-6">
        {/* Header */}
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="jobs-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Projects
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {boardOnly ? "Project board" : "Projects"}
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                {jobs.length} projects
                {stalledCount > 0 ? ` · ${stalledCount} need attention` : ""}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {boardOnly ? (
                <Button
                  data-testid="board-open-list"
                  variant="outline"
                  onClick={() => navigate("/projects")}
                  className="h-11 border-zinc-700 text-xs font-bold uppercase tracking-widest text-zinc-300 hover:text-white"
                >
                  <Rows size={16} weight="bold" className="mr-2" /> Project list
                </Button>
              ) : (
                <Button
                  data-testid="projects-open-board"
                  variant="outline"
                  onClick={() => navigate("/projects/board")}
                  className="h-11 border-zinc-700 text-xs font-bold uppercase tracking-widest text-zinc-300 hover:text-white"
                >
                  <SquaresFour size={16} weight="bold" className="mr-2" /> Board
                </Button>
              )}
              <Button
                data-testid="new-job-button"
                onClick={() => navigate("/projects/new")}
                className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
              >
                <Plus size={18} weight="bold" className="mr-2" /> New Project
              </Button>
            </div>
          </div>

          {/* Status summary strip — drives the board columns */}
          {boardOnly && (
          <div
            className="mt-4 flex flex-wrap gap-2 border-t border-zinc-800 pt-4"
            data-testid="jobs-status-strip"
          >
            <button
              type="button"
              data-testid="jobs-status-all"
              onClick={() => setStageFilter("All")}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                stageFilter === "All"
                  ? "border-indigo-500 bg-indigo-500/15 text-white"
                  : "border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              All
              <span className="font-mono-data text-[11px] text-zinc-500">
                {jobs.length}
              </span>
            </button>
            {JOB_STAGES.map((s) => {
              const n = jobs.filter((j) => j.stage === s).length;
              return (
                <button
                  key={s}
                  type="button"
                  data-testid={`jobs-status-chip-${s}`}
                  onClick={() =>
                    setStageFilter((cur) => (cur === s ? "All" : s))
                  }
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                    stageFilter === s
                      ? "border-indigo-500 bg-indigo-500/15 text-white"
                      : "border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  <span className={`h-2 w-2 rounded-full ${STAGE_DOT[s]}`} />
                  {s}
                  <span className="font-mono-data text-[11px] text-zinc-500">
                    {n}
                  </span>
                </button>
              );
            })}
            <button
              type="button"
              data-testid="jobs-stalled-chip"
              onClick={() => setOnlyStalled((v) => !v)}
              className={`ml-auto inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                onlyStalled
                  ? "border-red-500 bg-red-500/15 text-red-200"
                  : "border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              <span className="h-2 w-2 rounded-full bg-red-400" />
              Needs attention
              <span className="font-mono-data text-[11px]">{stalledCount}</span>
            </button>
            <button
              type="button"
              data-testid="jobs-losing-chip"
              onClick={() => setOnlyLosing((v) => !v)}
              title="Projects whose recorded costs exceed what was invoiced"
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                onlyLosing
                  ? "border-rose-500 bg-rose-500/15 text-rose-200"
                  : losingCount
                    ? "border-rose-500/40 text-rose-300 hover:bg-zinc-800"
                    : "border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              <TrendDown size={12} weight="bold" />
              Losing money
              <span className="font-mono-data text-[11px]">{losingCount}</span>
            </button>
            <div
              className="flex overflow-hidden rounded-lg border border-zinc-700"
              data-testid="jobs-pay-filter"
              title="Completed jobs: paid in full vs balance still due"
            >
              {[
                ["all", "Any $", null],
                ["paid", "Paid", paidCount],
                ["unpaid", "Unpaid", unpaidCount],
              ].map(([k, label, n]) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`jobs-pay-${k}`}
                  onClick={() => setPay(k)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                    pay === k
                      ? k === "unpaid"
                        ? "bg-amber-500/20 text-amber-200"
                        : "bg-indigo-500/15 text-white"
                      : "text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  {label}
                  {n != null && <span className="font-mono-data text-[11px]">{n}</span>}
                </button>
              ))}
            </div>
            <button
              type="button"
              data-testid="jobs-archived-toggle"
              onClick={() => setShowArchived((v) => !v)}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                showArchived
                  ? "border-zinc-500 bg-zinc-700/50 text-white"
                  : "border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              <Archive size={12} weight="bold" />
              {showArchived ? "Showing archived" : "Archived"}
            </button>
          </div>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {!boardOnly && (
          <SegTabs testId="jobs-tabs" tabs={TABS} value={tab} onChange={goTab} />
          )}

          {/* Search (board + list) */}
          {tab !== "field" && tab !== "list" && (
            <div className="relative min-w-0 flex-1 sm:max-w-xs">
              <MagnifyingGlass
                size={16}
                weight="bold"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
              />
              <Input
                data-testid="job-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search project, customer, address, code…"
                className="h-11 border-zinc-700 bg-zinc-900 pl-9 pr-9 text-sm"
              />
              {q && (
                <button
                  type="button"
                  data-testid="job-search-clear"
                  onClick={() => setQ("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Panels */}
        <div className="mt-6 min-w-0 overflow-x-hidden">
          {tab === "board" && (
            <JobStageBoard
              jobs={shown}
              stageFilter={stageFilter}
              onlyStalled={onlyStalled}
              onlyLosing={onlyLosing}
              onMove={move}
            />
          )}
          {tab === "list" && <Projects embedded />}
          {tab === "field" && <JobFieldView />}
        </div>
      </main>
    </div>
  );
}
